import React, { useEffect, useRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../../../../../translation/namespace';
import extendClassNameWithModifier from '../../../../../../../lib/bemUtils/bem';
import { LoadingSpinner } from '../../../../../../atoms/LoadingSpinner';
import ListBoxModifier from './ListBox.modifier';
import { StatusInfoError } from '../../../../../../../_lib/components/StatusInfo';

type ListBoxProps = {
  error?: string;
  id: string;
  selectedItemRef: ((instance: HTMLDivElement | null) => void) | React.RefObject<HTMLDivElement | null> | null;
  modifier?: string;
  loading: boolean;
  ariaHidden: boolean;
  children?: ReactNode[];
  onBlur: () => void;
};

function ListBox(props: ListBoxProps) {
  const { t } = useTranslation(Namespace.FormsComboBox);
  const listBoxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!props.ariaHidden && listBoxRef.current) {
      if (props.selectedItemRef && 'current' in props.selectedItemRef) {
        listBoxRef.current.scrollTop =
          // @ts-expect-error -- if offsetTop does note exist, fallback to 0 is ok
          (props.selectedItemRef.current?.offsetTop || 0) - (props.selectedItemRef.current?.parentNode?.offsetTop || 0);
      } else {
        listBoxRef.current.scrollTop = 0;
      }
    }
  });

  return (
    <div
      className={extendClassNameWithModifier('combobox__listbox', props.modifier || '')}
      // If list reads incorrectly in screen readers, see SIBZIEL-3250 first!
      ref={listBoxRef}
      id={`combobox-listbox-${props.id}`}
      role="listbox"
      aria-hidden={props.ariaHidden} // True if results not available
      aria-label={t('listboxLabel')}
      /* A scrollable box (with a max-height here) needs to be focusable when open */
      tabIndex={props.modifier === ListBoxModifier.MAX_HEIGHT_200 && !props.ariaHidden ? 0 : -1}
      onBlur={props.onBlur}
    >
      {props.error && (
        <div className="combobox__listbox-error">
          <StatusInfoError message={props.error} />
        </div>
      )}
      {!props.error && props.loading === true && (
        <div className="combobox__listbox-spinner">
          <LoadingSpinner />
        </div>
      )}
      {!props.error && props.loading === false && props.children}
    </div>
  );
}

export default ListBox;
